import pathnames from "@/pathnames";
import SearchBox from "./SearchBox";
import FilterMajorProjects from "./Filter";
import { IFilterData, IMajorProjectJob, MajorProjectJobResponse, MajorProjectSearchBody } from "@/types/majorProjects";
import { useEffect, useState } from "react";
import majorProjectsService from "@/services/majorProjectsService";
import { useMajorProjectStore } from "@/stores/majorProjectStore.ts";
import * as dateRangeStore from "@/hooks/useDateRangeStore";
import { useShallow } from "zustand/shallow";
import { useMutation } from "@tanstack/react-query";
import LoadingComponent from "@/components/common/loading_spinner/LoadingSpinner.tsx";
import BaseTable from "@/components/table-ant/table.tsx";
import { ColumnType } from "antd/es/table";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { createOnCellFunction, RenderWithFallback } from "@/components/table-ant/render-table-common";
import dayjs from "dayjs";
import { useResponsive } from "@/hooks/useResponsive.ts";
import { Button, Input, List, Skeleton } from "antd";
import { Images } from "@/constants/images.tsx";
import {
  JOBS,
  LAST_ACTIVITY_DATE,
  SEARCH_BY_PROJECT_NAME,
  SOMETHING_WENT_WRONG,
  START_DATE,
  TEXT_RESET,
  TOTAL_PROJECT,
  TOTAL_PROJECTS,
  VIEW_ITEMS,
  VIEW_JOBS
} from "@/constants/strings.ts";
import { ROUTES, THEME } from "@/constants";
import { useToast } from "@/components/common/toast/ToastProvider.tsx";
import { ToastType } from "@/components/common/toast/Toast.tsx";
import { DateRangeSelector } from "@/layouts/Mobile/ToolBox/DateRangeSelector.tsx";
import { MajorProjectFilter } from "@/layouts/Desktop/MajorProjects/MajorProjectFilter.tsx";
import { sortASC, sortDESC, TIME_FORMAT } from "@/constants/app_constants.ts";
import InfiniteScroll from "react-infinite-scroll-component";
import { SortIcon } from "@/components/common/SortIcon.tsx";
import { formatTimeDayMonthYear } from "@/components/table-ant/table-common";
import { MenuItem } from "@/stores/sidebarStore";
import EmptyBox from "@/components/empty-box";

const MajorProjectsDesktop = () => {
  const tabTitle = pathnames.majorProjects.main.name;
  const tabTitleMobile = pathnames.majorProjects.mainMobile.name;

  const { isMobile } = useResponsive();
  const [isFiltersModalVisible, setFiltersModalVisible] = useState(false);
  const { showToast } = useToast();
  const { startDate, endDate } = dateRangeStore.useDateRangeStoreMajor();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [actionColumnWidth, setActionColumnWidth] = useState(0);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const {
    companyId__in,
    id__in,
    search_text,
    filters,
    page,
    tempFilters,
    setFromDate,
    setToDate,
    setFilters,
    setTempFilters,
    setSearchText,
    setCompanyIdIn,
    setIdIn,
    setPage,
    getSearchParams,
    sortModel,
    setSortModel,
    setDataResponse,
    dataResponse,
    setPageSize,
    setOrderBy,
    setNameIn
  } = useMajorProjectStore(
    useShallow(state => ({
      order_by: state.order_by,
      pageSize: state.pageSize,
      page: state.page,
      fromDate: state.fromDate,
      toDate: state.toDate,
      search_text: state.search_text,
      companyId__in: state.companyId__in,
      id__in: state.id__in,
      filters: state.filters,
      tempFilters: state.tempFilters,
      setOrderBy: state.setOrderBy,
      setPageSize: state.setPageSize,
      setPage: state.setPage,
      setFromDate: state.setFromDate,
      setToDate: state.setToDate,
      setSearchText: state.setSearchText,
      setCompanyIdIn: state.setCompanyIdIn,
      setIdIn: state.setIdIn,
      setAllSearchParams: state.setAllSearchParams,
      getSearchParams: state.getSearchParams,
      resetSearchParams: state.resetSearchParams,
      setDataResponse: state.setDataResponse,
      setFilters: state.setFilters,
      setTempFilters: state.setTempFilters,
      sortModel: state.sortModel,
      setSortModel: state.setSortModel,
      dataResponse: state.dataResponse,
      setNameIn: state.setNameIn,
      name__in: state.name__in
    }))
  );

  const fetchMajorProjectsJobsMutation = useMutation<MajorProjectJobResponse, Error, MajorProjectSearchBody>({
    mutationFn: params => majorProjectsService.getMajorProjectsList(params),
    onSuccess: (data: MajorProjectJobResponse) => {
      setDataResponse(data);
    },
    onError: _ => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });

  const getFilterMutation = useMutation({
    mutationFn: majorProjectsService.getFilter,
    onSuccess: (data: IFilterData) => {
      setFilters(data);
      setTempFilters(data);
      const companyOptions: string[] = data.companies?.map(company => company.id) || [];
      const projectOptions: string[] = data.projectNames?.map(project => project.id) || [];
      const projectDisplayNames: string[] = data.projectNames?.map(project => project.displayName) || [];
      setCompanyIdIn(companyOptions);
      setIdIn(projectOptions);
      setNameIn(projectDisplayNames);
    },
    onError: _ => {
      showToast(ToastType.error, SOMETHING_WENT_WRONG);
    }
  });

  const handleFilterData = (needFetchFilter?: boolean) => {
    setFromDate(startDate?.format(TIME_FORMAT.DATE) ?? "");
    setToDate(endDate?.format(TIME_FORMAT.DATE) ?? "");
    setPage(1);
    setPageSize(20);
    const payload: MajorProjectSearchBody = getSearchParams();
    setIsFilterApplied(!(!payload.search_text && !payload.name__in && !payload.companyId__in));
    if (needFetchFilter) getFilterMutation.mutate({ fromDate: payload.fromDate || "", toDate: payload.toDate || "" });
    fetchMajorProjectsJobsMutation.mutate(payload);
  };

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === "Enter") {
      const payload: MajorProjectSearchBody = getSearchParams();
      setIsFilterApplied(!(!payload.search_text && !payload.name__in && !payload.companyId__in));
      fetchMajorProjectsJobsMutation.mutate(payload);
    }
  };

  useEffect(() => {
    setSearchText("");
    setFromDate(startDate?.format(TIME_FORMAT.DATE) ?? undefined);
    setToDate(endDate?.format(TIME_FORMAT.DATE) ?? undefined);
    const initialJobsPayload: MajorProjectSearchBody = getSearchParams();
    initialJobsPayload.order_by = "{ lastActivityDate: DESC }";
    setSortModel({ columnKey: "lastActivityDate", order: "DESC" });
    onClearFilters();
    getFilterMutation.mutate({
      fromDate: initialJobsPayload.fromDate ?? "",
      toDate: initialJobsPayload.toDate ?? ""
    });
    if (navigationType === "POP") {
      initialJobsPayload.name__in = [];
      initialJobsPayload.companyId__in = [];
      fetchMajorProjectsJobsMutation.mutate(initialJobsPayload);
    }

    const updateWidth = () => {
      if (window.innerWidth > 1280) {
        setActionColumnWidth(120);
      } else {
        setActionColumnWidth(180);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [location]);

  const getSortDirection = (name: string) => (sortModel.columnKey === name ? (sortModel.order === sortASC ? "ascend" : "descend") : null);

  const columns: ColumnType<IMajorProjectJob>[] = [
    {
      dataIndex: "id",
      key: "id",
      title: "Id",
      width: 1,
      hidden: true,
      render: item => <RenderWithFallback data={item} />
    },
    {
      dataIndex: "name",
      key: "name",
      title: "Project Name",
      width: 200,
      onCell: createOnCellFunction(200),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      render: item => <RenderWithFallback data={item} />,
      sortOrder: getSortDirection("name")
    },
    {
      dataIndex: "projectAddress",
      key: "projectAddress",
      title: "Project Address",
      width: 300,
      onCell: createOnCellFunction(300),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      sortOrder: getSortDirection("projectAddress"),
      render: item => <RenderWithFallback data={item} />
    },
    {
      dataIndex: "companyName",
      key: "companyName",
      title: "Company",
      width: 250,
      onCell: createOnCellFunction(250),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      sortOrder: getSortDirection("companyName"),
      render: item => <RenderWithFallback data={item} />
    },
    {
      dataIndex: "startDate",
      key: "startDate",
      title: "Start Date",
      width: 140,
      onCell: createOnCellFunction(140),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      sortOrder: getSortDirection("startDate"),
      render: item => <RenderWithFallback data={formatTimeDayMonthYear(item)} />
    },
    {
      dataIndex: "lastActivityDate",
      key: "lastActivityDate",
      title: "Last Activity Date",
      width: 180,
      onCell: createOnCellFunction(180),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      sortOrder: getSortDirection("lastActivityDate"),
      render: item => <RenderWithFallback data={formatTimeDayMonthYear(item)} />
    },
    {
      dataIndex: "numOfJobs",
      key: "numOfJobs",
      title: "Jobs",
      width: 80,
      onCell: createOnCellFunction(80),
      sorter: true,
      sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
      sortOrder: getSortDirection("numOfJobs"),
      render: item => <RenderWithFallback data={item} />
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      align: "center",
      width: actionColumnWidth,
      render: (_, record) => (
        <div className="flex gap-3 justify-center text-nowrap">
          <Link to={ROUTES.JOBS_LIST + `?tab=${MenuItem.majorProject}`} state={{ id: record.id, name: record.name, projectId: record.projectId }}>
            View Jobs
          </Link>
          <Link
            to={ROUTES.VIEW_PROJECT_ITEM_DETAIL + `?tab=${MenuItem.majorProject}`}
            state={{ id: record.id, name: record.name, projectId: record.projectId }}
          >
            View Items
          </Link>
        </div>
      )
    }
  ];

  const onClearFilters = () => {
    const companyOptions: string[] = filters.companies?.map(company => company.id) || [];
    const projectOptions: string[] = filters.projectNames?.map(project => project.id) || [];
    const projectDisplayNames: string[] = filters.projectNames?.map(project => project.displayName) || [];
    setCompanyIdIn(companyOptions);
    setIdIn(projectOptions);
    setNameIn(projectDisplayNames);
    setTempFilters({
      companies: filters.companies,
      projectNames: filters.projectNames
    });
    setSearchText("");
    handleFilterData();
  };

  const tableHeader = (isMobile: boolean) =>
    isMobile ? (
      <div className="flex w-full justify-between items-center pl-3 mb-3 mt-[24px] pr-3">
        <div className="font-light text-sm" style={{ color: THEME.COLORS.TABLE_TEXT }}>
          {dataResponse.meta.totalRecords} {dataResponse.meta.totalRecords === 1 ? TOTAL_PROJECT : TOTAL_PROJECTS}
        </div>
        {isFilterApplied && (
          <div className="text-orange-600 font-semibold text-sm cursor-pointer" onClick={onClearFilters}>
            {TEXT_RESET}
          </div>
        )}
      </div>
    ) : (
      <div className="flex w-full justify-between items-center mb-3 mt-[20px]">
        <div className="font-semibold text-sm" style={{ color: THEME.COLORS.TABLE_TEXT }}>
          {dataResponse.meta.totalRecords} {dataResponse.meta.totalRecords === 1 ? TOTAL_PROJECT : TOTAL_PROJECTS}
        </div>
      </div>
    );

  // #region Lazy Loading
  const handleLoadMore = () => {
    if (!dataResponse.meta.hasNextPage) return;
    setPage((page ?? 0) + 1);
    setPageSize(20);
    fetchMajorProjectsJobsMutation.mutate(getSearchParams());
  };
  // #endregion Lazy Loading

  // #region Sort
  const handleCreateSorter = (_pagination: any, _filters: any, sorter: any) => {
    const { field, order } = sorter;
    let sortDirection = sortModel.order === sortASC ? sortDESC : sortASC;

    let sortBy = { [sortModel.columnKey]: sortDirection };

    if (field && order) {
      sortDirection = order === "ascend" ? sortASC : sortDESC;
      sortBy = { [field === "companyName" ? "company.name" : field]: sortDirection };
    }

    setSortModel({
      order: sortDirection,
      columnKey: sorter.columnKey ?? sortModel.columnKey
    });

    setPage(1);
    setPageSize(20);
    setOrderBy(Object.keys(sortBy).length > 0 ? JSON.stringify(sortBy) : JSON.stringify({ [sortModel.columnKey]: sortModel.order }));
    fetchMajorProjectsJobsMutation.mutate(getSearchParams());
  };
  // #endregion Sort

  // #region Render Desktop
  const renderDesktopLayout = () => {
    return (
      <div className="mx-8 my-5">
        <div className="text-xl font-semibold leading-loose">{tabTitle}</div>

        <SearchBox
          searchInputValue={search_text ?? ""}
          setSearchInputValue={setSearchText}
          handleFilterData={flag => handleFilterData(flag)}
          isUiConfig={false}
          columnsUIConfig={[]}
          selectedColumns={[]}
          fromDate={startDate?.format(TIME_FORMAT.DATE) ?? ""}
          toDate={endDate?.format(TIME_FORMAT.DATE) ?? ""}
        />
        <FilterMajorProjects
          handleFilterData={handleFilterData}
          filters={filters}
          selectedCompany={companyId__in}
          setSelectedCompany={setCompanyIdIn}
          setTempFilters={setTempFilters}
          selectedProject={id__in}
          setSelectedProject={setIdIn}
          setSearchInputValue={setSearchText}
          tempFilters={tempFilters}
        />
        {tableHeader(false)}
        <BaseTable
          columns={columns}
          rowKey="id"
          dataSource={dataResponse.items ?? []}
          style={{ marginTop: 12 }}
          onLoadMore={handleLoadMore}
          onChange={handleCreateSorter}
          loading={fetchMajorProjectsJobsMutation.isPending}
        />
      </div>
    );
  };
  // #endregion Render Desktop

  // #region Render Mobile
  const renderMobileLayout = () => {
    const buildMobileHeader = () => (
      <div className="self-stretch justify-between items-center inline-flex overflow-hidden w-full pl-3 mt-6 pr-3">
        <div className="flex self-stretch relative justify-start items-start">
          <h1 className="font-semibold text-[20px] items-start flex justify-start">{tabTitleMobile}</h1>
        </div>
      </div>
    );

    const buildMobileDateSection = () => (
      <div className="mx-3 mt-2">
        <div className="flex items-center px-1 py-0 space-x-1 border border-gray-300 rounded-md" style={{ height: "40px" }}>
          <DateRangeSelector handleFilterData={() => handleFilterData(true)} label="Last Activity Date" isMobile={true} />
        </div>
      </div>
    );

    const buildMobileFilterSection = () => (
      <div className="flex h-10 gap-3 mt-5 ml-3 mr-3">
        <Input
          placeholder={SEARCH_BY_PROJECT_NAME}
          prefix={<img src={Images.IconSearch} alt="search" className="ml-1 mr-1" />}
          className=""
          value={search_text}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          icon={
            <div className="relative w-5 h-5">
              <img src={Images.IconFilter} alt="filter" className="w-full h-full" />
              {isFilterApplied && <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>}
            </div>
          }
          className="rounded-md border border-gray-300 cursor-pointer"
          style={{
            height: "40px",
            width: "40px",
            color: THEME.COLORS.PRIMARY
          }}
          onClick={() => setFiltersModalVisible(true)}
        ></Button>
      </div>
    );

    const MajorProjectJobCard = (job: IMajorProjectJob) => {
      return (
        <div key={job.id} className="bg-white p-4 rounded-md mb-3 border-1 border-gray-200">
          <div className="font-semibold text-[16px] text-gray-900">{job.name || job.companyName || "N/A"}</div>

          <div className="flex justify-between mt-2">
            <div className="flex-1">
              <div className="text-gray-700 text-xs">{LAST_ACTIVITY_DATE}</div>
              <div className="text-gray-900 text-sm font-semibold mt-1">{dayjs(job.lastActivityDate).format("DD/MM/YYYY")}</div>
            </div>
            <div className="flex-1">
              <div className="text-gray-700 text-xs">{START_DATE}</div>
              <div className="text-gray-900 text-sm font-semibold mt-1">{dayjs(job.startDate).format("DD/MM/YYYY")}</div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-gray-700 text-xs">{JOBS}</div>
            <div className="text-gray-900 text-sm font-semibold mt-1">{job.numOfJobs}</div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              className="h-full flex-1 rounded-md border border-gray-300 cursor-pointer !font-semibold !text-sm"
              style={{
                height: "40px",
                color: THEME.COLORS.ORANGE_600
              }}
              onClick={() => {
                navigate(ROUTES.JOBS_LIST + `?tab=${MenuItem.majorProject}`, {
                  state: { id: job.id, name: job.name, projectId: job.projectId }
                });
              }}
            >
              {VIEW_JOBS}
            </Button>
            <Button
              className="h-full flex-1 rounded-md border border-gray-300 cursor-pointer !font-semibold !text-sm"
              style={{
                height: "40px",
                color: THEME.COLORS.ORANGE_600
              }}
              onClick={() => {
                navigate(ROUTES.VIEW_PROJECT_ITEM_DETAIL + `?tab=${MenuItem.majorProject}`, {
                  state: { id: job.id, name: job.name, projectId: job.projectId }
                });
              }}
            >
              {VIEW_ITEMS}
            </Button>
          </div>
        </div>
      );
    };

    return (
      <div>
        {buildMobileHeader()}
        {buildMobileDateSection()}
        {buildMobileFilterSection()}
        {tableHeader(true)}
        <div className="mx-3">
          <div id="scrollableDiv">
            <InfiniteScroll
              next={handleLoadMore}
              hasMore={dataResponse.meta.hasNextPage}
              dataLength={dataResponse.items.length}
              loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
            >
              <List
                split={false}
                itemLayout="vertical"
                dataSource={dataResponse.items ?? []}
                locale={{
                  emptyText: <EmptyBox loading={false} />
                }}
                renderItem={job => (
                  <List.Item key={job.id} style={{ padding: 0, borderBottom: "none" }}>
                    {" "}
                    {MajorProjectJobCard(job)}
                  </List.Item>
                )}
              />
            </InfiniteScroll>
          </div>
        </div>
        <MajorProjectFilter
          isVisible={isFiltersModalVisible}
          onCancel={() => setFiltersModalVisible(false)}
          onClearFilters={onClearFilters}
          onApplyFilters={handleFilterData}
        />
        {(fetchMajorProjectsJobsMutation.isPending || getFilterMutation.isPending) && <LoadingComponent isPending={true} />}
      </div>
    );
  };
  // #endregion Render Mobile
  return isMobile ? renderMobileLayout() : renderDesktopLayout();
};

export default MajorProjectsDesktop;
